import { useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { AuthLayout } from '../../components/AuthLayout'
import { Button } from '../../components/Button'
import { FormGateFeedFilters } from '../../components/ConnectedFormGateFeedFilters'
import { ConnectedObservationTags } from '../../components/ConnectedObservationTags'
import { ImageGallery } from '../../components/ImageViewer'
import { ObservationDetails } from '../../components/ObservationDetailsPanel'
import { PageBody } from '../../components/PageBody'
import { PageLabel } from '../../components/PageLabel'
import { PaginationContainer } from '../../components/PaginationContainer'
import { PanelGateFeedEdit } from '../../components/PanelGateFeedEdit'
import { Spacer } from '../../components/Spacer'
import { TabContainer } from '../../components/TabContainer'
import { TableGateFeed, ViewableImage } from '../../components/TableGateFeed'
import { YardScoreboard, YardScoreboardValue } from '../../components/YardScoreboard'
import { PAGE_PATHS } from '../../config/pagePaths'
import { getObservationTemplate } from '../../content/tableTemplates'
import { useGateObservations } from '../../hooks/useGateObservations'
import { useMessage } from '../../hooks/useMessage'
import { useObservationExports } from '../../hooks/useObservationExports'
import { usePanel } from '../../hooks/usePanel'
import { useYardReport } from '../../hooks/useYardReport'
import type {
  GateFeedColumnKey,
  GateObservation,
  ObservationDraft,
} from '../../models/client/observation'
import { NullablePartial } from '../../models/tools'
import { selectCompany } from '../../store/company'
import { selectSiteDirectory, selectSiteLoading } from '../../store/sites'
import { selectUser, selectUserInit } from '../../store/user'
import { SPACING } from '../../styles/mixins/constants'
import { device } from '../../styles/mixins/device'
import { isEqual } from '../../util/isEqual'

const Container = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-flow: column nowrap;
`

const StyledPageBody = styled(PageBody)`
  overflow: auto;
  min-height: 0px;
  flex: 1 1 auto;
`

const HeaderGrid = styled.div`
  display: grid;
  grid-gap: ${SPACING[3]};
  grid-template-areas:
    'label'
    'yardreport'
    'filters';
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  ${device.desktop`
  grid-template-columns: 1fr auto;
      grid-template-areas:
      'label yardreport'
      'filters filters';
  grid-row-gap: ${SPACING[6]};
  `}
`

const FilterInterface = styled(FormGateFeedFilters)`
  grid-area: filters;
`
const YardReportContainer = styled.div`
  grid-area: yardreport;
  display: flex;
  justify-content: flex-start;
  flex: 1 1 auto;
  ${device.desktop`
    justify-content: flex-end;
  `}
`

const StyledPageLabel = styled(PageLabel)`
  grid-area: label;
`

const Row = styled.div`
  display: flex;
  flex-flow: row;
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
`

const DEFAULT_COLUMNS: Array<GateFeedColumnKey> = [
  'assetPreviewUrl',
  'loadNumber',
  'loadDwellTimeHr',
  'hasSeal',
  'loadRemarks',
  'chassisNumber',
  'chassisDwellTimeHr',
  'vehicleNumber',
  'vehicleCarrierName',
  // 'vehicleDwellTimeHr',
]

export function Reports(): JSX.Element {
  const navigate = useNavigate()
  const { exportData } = useObservationExports()
  const [visibleImageData, setViewableImageData] = useState<null | ViewableImage>(null)
  const { PanelComponent: EditFeedPanelContainer, setPanelVisibility: setEditFeedPanelVisible } =
    usePanel<Array<GateFeedColumnKey>>()
  const { PanelComponent: EditItemPanelContainer, setPanelVisibility: setEditItemPanelVisible } =
    usePanel<{
      isDuplicate?: boolean
      isNew?: boolean
      observation?: GateObservation
    }>({
      onClose: () => navigate(PAGE_PATHS.reports),
    })
  const messenger = useMessage()
  const user = useSelector(selectUser, isEqual)
  const company = useSelector(selectCompany, isEqual)
  const siteDirectory = useSelector(selectSiteDirectory, isEqual)
  const isLoading = useSelector(selectSiteLoading, shallowEqual)
  const isInit = useSelector(selectUserInit, shallowEqual)

  let includeVehicles = false
  if (company && company.settings?.includeYrVehicles) {
    includeVehicles = true
  }

  const {
    filters,
    gateObservations,
    isLoading: dataIsLoading,
    pageSize,
    page,
    totalItems,
    sort,
    userColumnKeys,
    isColumnSettingsLoading,
    setPage,
    editObservation,
    deleteObservation,
    outgateObservation,
    removeFilter,
    addFilter,
    clearAllFilters,
    addObservation,
    setSort,
    setReportUserColumns: setUserColumns,
    refresh,
  } = useGateObservations({
    defaultColumns: DEFAULT_COLUMNS,
    reportType: 'yardreport',
    includeVehicles,
  })

  let allowedSites = {}
  if (siteDirectory && user && user.meta?.sites) {
    Object.keys(siteDirectory).forEach((k) => {
      if (user.meta?.sites.includes(siteDirectory[k].alias)) {
        allowedSites[k] = siteDirectory[k]
      }
    })
  } else {
    allowedSites = siteDirectory
  }

  const { data: statData, isLoading: statsLoading } = useYardReport({ filters })

  const handleSaveObservationFor = (data: { isNew?: boolean; observation?: GateObservation }) => {
    return async (draft: NullablePartial<ObservationDraft>): Promise<void> => {
      try {
        const isAddAction = !!data?.isNew

        if (isAddAction) {
          await addObservation(draft, {}, { report: true })
        } else {
          await editObservation(
            data.observation?.observationId,
            draft,
            {
              gateId: data?.observation.gateId,
              chassisId: data?.observation.chassisId,
              vehicleId: data?.observation.vehicleId,
              loadId: data?.observation.loadId,
            },
            { report: true }
          )
        }

        messenger.success('Observation saved')

        setEditItemPanelVisible(false)

        navigate(PAGE_PATHS.reports)

        await refresh()
      } catch (e) {
        if (e instanceof Error) {
          // eslint-disable-next-line no-console
          console.error(`ERROR handleSaveObservation: ${e.message}`, e)
        }
        messenger.danger('Something went wrong, could not remove observation')
      }
    }
  }

  const handleDeleteObservation = async (
    observationId: string,
    observation: any
  ): Promise<void> => {
    try {
      await deleteObservation(observationId, observation, {}, { report: true })

      messenger.success('Observation deleted')

      setEditItemPanelVisible(false)

      navigate(PAGE_PATHS.reports)
    } catch (e) {
      if (e instanceof Error) {
        // eslint-disable-next-line no-console
        console.error(`ERROR handleDeleteObservation: ${e.message}`)
      }
      messenger.danger('Something went wrong, could not remove observation')
    }
  }

  const handleDataExport = (): Promise<void> => {
    try {
      exportData({
        filters,
        report: true,
        includeVehicles,
      })
    } catch (e) {
      if (e instanceof Error) {
        // eslint-disable-next-line no-console
        console.error(`ERROR handleDataExport: ${e.message}`)
      }
      messenger.danger('Something went wrong, could not export data')
    } finally {
      // eslint-disable-next-line no-unsafe-finally
      return Promise.resolve()
    }
  }

  const handleOutgateObservation = async (
    observationId: string,
    observation: GateObservation,
    outgateDateTime: string
  ): Promise<void> => {
    try {
      await outgateObservation(observationId, observation, outgateDateTime, {})
      messenger.success('Observation outgated')
      setEditItemPanelVisible(false)
      navigate(PAGE_PATHS.gateFeed)
    } catch (e) {
      if (e instanceof Error) {
        // eslint-disable-next-line no-console
        console.error(`ERROR handleDeleteObservation: ${e.message}`)
      }
      messenger.danger('Something went wrong, could not remove observation')
    }
  }

  const tableLoading = isLoading || dataIsLoading
  const yardData: YardScoreboardValue[] = statData.map((item) => ({
    label: item.label,
    value: item.count,
  }))

  return (
    <AuthLayout>
      <StyledPageBody>
        <ImageGallery
          visibleData={
            visibleImageData
              ? {
                  images: visibleImageData.images,
                  path: visibleImageData.path,
                  imagePaths: visibleImageData.imagePaths,
                  id: visibleImageData.observationId,
                }
              : null
          }
          onClose={() => setViewableImageData(null)}
        />

        <EditFeedPanelContainer
          renderChild={({ data: columns }) =>
            columns ? (
              <PanelGateFeedEdit
                toastMessageOnUpdate="Report preferences updated"
                columns={columns}
                title="Report Settings"
                onClose={() => {
                  setEditFeedPanelVisible()
                }}
                onSave={(nextColumns) => setUserColumns(nextColumns)}
                isLoading={isColumnSettingsLoading}
              />
            ) : (
              <div />
            )
          }
        />

        <EditItemPanelContainer
          renderChild={({ data }) => (
            <ObservationDetails
              data={data?.observation || undefined}
              isDuplication={data?.isDuplicate}
              isNew={data?.isNew}
              isReport
              template={getObservationTemplate('gatefeed')}
              onSave={handleSaveObservationFor(data)}
              onExit={() => {
                navigate(PAGE_PATHS.reports)

                setEditItemPanelVisible(false)
              }}
              onDelete={(id) => {
                return handleDeleteObservation(id, data?.observation)
              }}
              onOutgate={(id, outgateDateTime) => {
                return handleOutgateObservation(id, data?.observation, outgateDateTime)
              }}
              onViewImage={setViewableImageData}
            />
          )}
        />

        <Container>
          <HeaderGrid>
            <StyledPageLabel
              title="Reports"
              description="Generate a custom yard inventory report."
            />

            <YardReportContainer>
              <YardScoreboard
                values={yardData}
                isLoading={statsLoading}
                includeStatVehicles={includeVehicles}
              />
            </YardReportContainer>

            <FilterInterface
              sites={allowedSites || {}}
              filters={filters}
              onAddFilter={addFilter}
              onRemoveFilter={removeFilter}
              fields={['location', 'gate', 'search', 'bare']}
            />
          </HeaderGrid>

          <Spacer vertical size={4} />

          <Row>
            <ConnectedObservationTags
              filters={filters}
              sort={sort}
              onRemoveFilter={removeFilter}
              onRemoveSort={() => setSort(null)}
              onClearAllFilters={clearAllFilters}
            />
            <Button
              type="primary"
              label="Add Asset"
              iconKey="Plus"
              size="large"
              onClick={() => {
                setEditItemPanelVisible({
                  isVisible: true,
                  data: {
                    isNew: true,
                  },
                })
              }}
              disabled={false}
            />
          </Row>

          <TabContainer
            tabs={[
              {
                title: 'Yard Report',
                key: 'yardreport',
                Component: (
                  <PaginationContainer
                    totalItems={totalItems}
                    currentPage={page}
                    pageSize={pageSize}
                    disabled={!isInit || isLoading}
                    onPageSelect={(index) => setPage(index)}
                  >
                    <TableGateFeed
                      disableDuplicate
                      disableEditTableRow={false}
                      sort={sort}
                      observations={gateObservations}
                      onViewImage={setViewableImageData}
                      isLoading={tableLoading}
                      columnKeys={userColumnKeys}
                      renderExitStatus
                      onEdit={(draft) => {
                        setEditItemPanelVisible({
                          isVisible: true,
                          data: {
                            observation: draft,
                          },
                        })
                        navigate(`${PAGE_PATHS.reports}?observationId=${draft.observationId}`)
                      }}
                      onDuplicate={(draft) => {
                        setEditItemPanelVisible({
                          isVisible: true,
                          data: {
                            isDuplicate: true,
                            observation: draft,
                          },
                        })
                      }}
                      onControlClick={() =>
                        setEditFeedPanelVisible({
                          isVisible: true,
                          data: userColumnKeys,
                        })
                      }
                      onDownload={() => handleDataExport()}
                      onSort={setSort}
                    />
                  </PaginationContainer>
                ),
              },
            ]}
            activeKey="yardreport"
          />
        </Container>
      </StyledPageBody>
    </AuthLayout>
  )
}
